<template>
  <div>
    <div class="modal is-active">
      <loading-component v-if="loading" />
      <div class="modal-background" @click="!loading && close()"></div>
      <div class="modal-content" v-if="!loading">
        <div class="box tracking-modal">

          <h3 class="title is-4 has-text-centered mb-1">
            Seguimiento del lead
          </h3>
          <p class="has-text-centered has-text-grey mb-4" v-if="recordLabel">
            {{ recordLabel }}
          </p>

          <div class="columns is-multiline">
            <div
              v-for="field in visibleFields"
              :key="field.key"
              class="column"
              :class="field.fullWidth ? 'is-12' : 'is-6'"
            >
              <div class="field">
                <label class="label">{{ field.label }}</label>

                <div v-if="field.type === 'select'" class="control">
                  <div class="select is-fullwidth">
                    <select v-model="formData[field.key]">
                      <option :value="null">Sin definir</option>
                      <option
                        v-for="opt in optionsFor(field)"
                        :key="opt.slug"
                        :value="opt.slug"
                      >
                        {{ opt.nombre }}
                      </option>
                    </select>
                  </div>
                </div>

                <div v-else-if="field.type === 'multiselect'" class="control">
                  <div class="select is-multiple is-fullwidth">
                    <select multiple v-model="formData[field.key]">
                      <option
                        v-for="opt in optionsFor(field)"
                        :key="opt.slug"
                        :value="opt.slug"
                      >
                        {{ opt.nombre }}
                      </option>
                    </select>
                  </div>
                  <p class="help">Mantén presionado Ctrl / Cmd para seleccionar múltiples</p>
                </div>

                <div v-else-if="field.type === 'boolean'" class="control">
                  <label class="checkbox">
                    <input type="checkbox" v-model="formData[field.key]" />
                    {{ field.checkboxLabel || 'Sí' }}
                  </label>
                </div>

                <div v-else-if="field.type === 'textarea'" class="control">
                  <textarea
                    class="textarea"
                    rows="3"
                    v-model="formData[field.key]"
                    placeholder="Escribe aquí..."
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-if="error" class="notification is-danger is-light mt-3">
            {{ error }}
          </div>

          <div class="columns mt-5">
            <div class="column">
              <button
                class="button is-danger is-outlined is-fullwidth is-rounded"
                :disabled="saving"
                @click="close"
              >
                Cancelar
              </button>
            </div>
            <div class="column">
              <button
                class="button is-success is-fullwidth is-rounded"
                :class="{ 'is-loading': saving }"
                :disabled="saving"
                @click="confirm"
              >
                Guardar
              </button>
            </div>
          </div>

        </div>
      </div>
      <button
        class="modal-close is-large"
        aria-label="close"
        :disabled="loading"
        @click="!loading && close()"
      ></button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { TRACKING_FORMS } from '@/lib/formTracking'

export default {
  name: 'FormTrackingModal',

  components: {
    LoadingComponent: () => import('@/components/ui/Loading')
  },

  props: {
    close: { type: Function, required: true },
    action: { type: Function, required: true },
    recordId: { type: [Number, String], required: true },
    // Sumar un formulario nuevo = agregar su config en @/lib/formTracking
    formType: { type: String, default: 'landing' },
    recordLabel: { type: String, default: '' }
  },

  data () {
    return {
      loading: false,
      saving: false,
      error: '',
      admins: [],
      formData: {}
    }
  },

  computed: {
    formConfig () {
      return TRACKING_FORMS[this.formType] || []
    },
    visibleFields () {
      return this.formConfig.filter(field => !field.showWhen || field.showWhen(this.formData))
    }
  },

  watch: {
    'formData.status' (status) {
      if (status !== 'rechazado') {
        this.formData.rejection_reason = null
      }
    }
  },

  async mounted () {
    this.loading = true
    this.formData = this.emptyFormData()

    try {
      const [admins, tracking] = await Promise.all([
        this.getAdminOptions(),
        this.getFormTracking({ formType: this.formType, recordId: this.recordId })
      ])

      this.admins = admins || []

      if (tracking) {
        this.formData = {
          ...this.emptyFormData(),
          ...this.formConfig.reduce((acc, field) => {
            const value = tracking[field.key]
            if (field.type === 'multiselect') acc[field.key] = Array.isArray(value) ? value : []
            else if (field.type === 'textarea') acc[field.key] = value || ''
            else if (field.type === 'boolean') acc[field.key] = !!value
            else acc[field.key] = value || null
            return acc
          }, {})
        }
      }
    } catch (e) {
      console.error('Error fetching form tracking:', e)
      this.error = 'No se pudo cargar el seguimiento.'
    } finally {
      this.loading = false
    }
  },

  methods: {
    ...mapActions(['getAdminOptions', 'getFormTracking']),

    emptyFormData () {
      return this.formConfig.reduce((acc, field) => {
        if (field.type === 'multiselect') acc[field.key] = []
        else if (field.type === 'textarea') acc[field.key] = ''
        else if (field.type === 'boolean') acc[field.key] = false
        else acc[field.key] = null
        return acc
      }, {})
    },

    optionsFor (field) {
      if (field.optionsSource === 'admins') {
        return this.admins.map(admin => ({ slug: admin.id, nombre: admin.name }))
      }
      return field.options || []
    },

    async confirm () {
      this.error = ''
      this.saving = true

      try {
        const saved = await this.action({ ...this.formData })
        if (saved === false) {
          this.error = 'No se pudo guardar el seguimiento.'
          return
        }
        this.close()
      } catch (e) {
        this.error = 'No se pudo guardar el seguimiento.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.tracking-modal {
  max-width: 700px;
  margin: auto;
}

.select.is-multiple select {
  min-height: 140px;
}
</style>
